import React from 'react';
import { Maximize2 } from 'lucide-react';
import { WorkflowCanvasInspector } from './workflow-canvas-inspector';
import { WorkflowRunStepStackContent } from './workflow-run-step-stack-view';
import type { WorkflowRunStepViewPayload } from './workflow-artifact-views';

export type WorkflowRunInspectorStep = {
  view: WorkflowRunStepViewPayload;
  title: string;
};

/**
 * A run step's result, in the canvas inspector — so the graph stays on screen
 * while you read it.
 *
 * Worth more here than in the editor: what you want when a run fails is the
 * error *and* where that step sits in the run. Selecting the node highlights it
 * and its edges, so the panel and the graph point at the same thing.
 *
 * Draws its own `header` because a step view has no chrome of its own, unlike the
 * editor's config panel.
 *
 * `onExpand` pushes the same content as a full-panel view. That is the one real
 * concession to content size: a config form is a few fields, but a step is a JSON
 * blob or a whole agent conversation, and those outgrow a docked panel. The
 * full-panel step view keeps earning its place rather than being replaced.
 */
export const WorkflowRunGraphInspector: React.FC<{
  step: WorkflowRunInspectorStep | null;
  onClose: () => void;
  onExpand: (step: WorkflowRunInspectorStep) => void;
}> = ({ step, onClose, onExpand }) => (
  <WorkflowCanvasInspector
    open={step !== null}
    onClose={onClose}
    {...(step !== null
      ? {
          header: {
            title: step.title,
            actions: (
              <button
                type="button"
                title="Open full width"
                aria-label="Open full width"
                onClick={() => onExpand(step)}
                className="text-muted-foreground hover:bg-muted hover:text-foreground flex size-7 shrink-0 items-center justify-center rounded-md transition-colors"
              >
                <Maximize2 className="size-4" />
              </button>
            ),
          },
        }
      : {})}
  >
    {step !== null ? <WorkflowRunStepStackContent view={step.view} /> : null}
  </WorkflowCanvasInspector>
);
